import { PlanTrailActionContext } from "./types";
import { StageConnector } from "../../../services/stage-connectors";
import { StageDestination, stageEndForDay, stageStartForDay } from "../../../services/stage-destinations";
import { MarkerFeature, MarkerGeometry, MarkerProperties } from "../../../../../utils/models/MarkerFeature";
import { generateID } from "../../../../../utils/helpers/IDGenerator";
import { AddMarkerCommand } from "../../undo/commands/AddMarkerCommand";

/**
 * Stage destinations — store actions.
 *
 * Attaching moves the day's END marker exactly onto the item's coordinates
 * (re-routing the last segment via the normal markerMoved path) and records
 * the destination. When the day was connected to the next one, the next
 * day's START marker moves too, so the days stay joined on the item.
 *
 * Detection/validity rules are pure and live in
 * services/stage-destinations.ts; drag-to-attach interaction lives in
 * markers.ts / stage-connectors.ts.
 */
export const stageDestinationActions = {
  async attachStageDestination(
    { state, getters, commit, dispatch }: PlanTrailActionContext,
    payload: { dayNumber: number; destination: StageDestination }
  ): Promise<boolean> {
    const { dayNumber, destination } = payload;
    const stageEnd = stageEndForDay(state.routes, dayNumber);
    if (!stageEnd) return false;

    const route = state.routes[stageEnd.routeKey];
    if (!route) return false;

    const target: [number, number] = [destination.coordinates[0], destination.coordinates[1]];

    // The route doesn't have a real END yet — just its start point (with
    // only one marker, "the day's end" IS the start marker — there's no
    // second point to distinguish them). Attaching here must EXTEND the
    // route to end at this item, the same as "Add control point" would,
    // rather than moving the lone marker onto it — which would just
    // relocate the start, not create an end.
    if (route.markers.length < 2) {
      const win = window as any;
      if (route.isReference &&
          typeof win.canEditReference === 'function' && !win.canEditReference(route)) {
        if (typeof win.checkReferenceEditWarning === 'function') {
          win.checkReferenceEditWarning(route);
        }
        return false;
      }

      const startMarker = route.markers[0];
      if (!startMarker) return false;

      const newMarker = new MarkerFeature(
        new MarkerGeometry([target[0], target[1]]),
        new MarkerProperties(generateID(), state.isSnap, Date.now().toString(), 0)
      );

      const markerIndex = route.markers.length;
      route.markers.push(newMarker);

      const lineString = await dispatch('createPolylineGeoJson', { startMarker, endMarker: newMarker });
      route.lineStrings.push(lineString);
      route.updated_at = new Date();

      state.undoManager.push(new AddMarkerCommand({
        routeKey: stageEnd.routeKey,
        markerIndex,
        marker: newMarker,
        lineString,
      }));

      commit('setStageDestination', { dayNumber, destination: { ...destination, coordinates: target } });
      await dispatch('updateRouteAndMarkers');
      return true;
    }

    const marker = route.markers[stageEnd.markerIndex];
    if (!marker) return false;

    // Read the connector BEFORE moving anything — once the end marker moves
    // away, the pair may exceed the connector threshold and stop reporting.
    const connector: StageConnector | undefined = (getters.stageConnectors as StageConnector[])
      .find(c => c.prevRouteKey === stageEnd.routeKey);

    // Locked mode: the frozen master trail never reroutes. Attaching a
    // destination here means "this boundary's off-trail stop is X" —
    // reposition it via the same slice+detour path a manual boundary drag
    // uses, instead of moving the main-line markers/rerouting them.
    if (state.routeLocked) {
      await dispatch('applyLockedBoundaryDrag', {
        boundaryDay: dayNumber,
        prevRouteKey: stageEnd.routeKey,
        nextRouteKey: connector ? connector.nextRouteKey : null,
        targetCoord: target,
      });
      commit('setStageDestination', { dayNumber, destination: { ...destination, coordinates: target } });
      await dispatch('updateRouteAndMarkers');
      return true;
    }

    // Move the marker first, then record the destination: markerMoved
    // refreshes stage decorations, which prune destinations whose end
    // marker isn't on the item.
    const oldPosition = [...marker.markerGeometry.coordinates];
    marker.markerGeometry.coordinates = [...target];
    commit('setStageDestination', { dayNumber, destination: { ...destination, coordinates: target } });
    await dispatch('markerMoved', { marker, oldPosition, routeKey: stageEnd.routeKey });

    // Keep connected days joined — bring the next day's start along
    if (connector) {
      const nextRoute = state.routes[connector.nextRouteKey];
      const nextMarker = nextRoute?.markers[0];
      if (nextMarker) {
        const nextOldPosition = [...nextMarker.markerGeometry.coordinates];
        nextMarker.markerGeometry.coordinates = [...target];
        await dispatch('markerMoved', { marker: nextMarker, oldPosition: nextOldPosition, routeKey: connector.nextRouteKey });
      }
    }

    // Rebuild the consolidated markers so the attached end marker is
    // suppressed beneath its destination ring
    await dispatch('addConsolidatedMarkersToMap');

    return true;
  },

  /**
   * Remove a day's destination WITHOUT moving any marker — the badge goes
   * back to its normal form and the day loses its destination row.
   */
  async detachStageDestination({ state, commit, dispatch }: PlanTrailActionContext, dayNumber: number) {
    if (!(dayNumber in state.stageDestinations)) return;
    commit('removeStageDestination', dayNumber);

    // Refresh the decorations that render differently while attached
    await dispatch('addConsolidatedMarkersToMap');
    await dispatch('updateStageConnectorsOnMap');
    await dispatch('updateTrailEndpointMarkersOnMap');
  },

  /**
   * Attach a day's stage-start (the first marker of that day's first
   * main-line route) to a map item — the "set as start point" action from
   * the POI/waypoint/highlight dialogs, called right after that first marker
   * is created (already sitting exactly on the item, so nothing needs to
   * move) or, potentially, on an existing start marker dragged onto an item
   * later (mirrors attachStageDestination's move-then-record behaviour).
   * `dayNumber` is whichever day is actually being edited (its own active
   * empty route, or the route a start marker was dragged from) — NOT
   * necessarily the plan's first day, since a later day can have its own
   * independent start when it isn't chained onto the previous day's end.
   * Renders as a ring around the item's icon, same as an attached stage end
   * — see updateTrailEndpointMarkersOnMap.
   */
  async attachTrailStart(
    { state, getters, commit, dispatch }: PlanTrailActionContext,
    payload: { dayNumber: number; destination: StageDestination }
  ): Promise<boolean> {
    const { dayNumber, destination } = payload;
    const start = stageStartForDay(state.routes, dayNumber);
    if (!start) return false;

    const route = state.routes[start.routeKey];
    const marker = route?.markers[start.markerIndex];
    if (!marker) return false;

    const target: [number, number] = [destination.coordinates[0], destination.coordinates[1]];
    const current = marker.markerGeometry.coordinates;
    const alreadyThere = current[0] === target[0] && current[1] === target[1];

    // Locked mode: the frozen master trail never reroutes. Attaching a
    // day's start repositions the boundary just before it (0 when it's the
    // very first day — there's no route before it, so prevRouteKey is null)
    // via the same slice+detour path a manual boundary drag uses, instead of
    // moving the route's start marker directly.
    if (state.routeLocked && !alreadyThere) {
      const connector: StageConnector | undefined = (getters.stageConnectors as StageConnector[])
        .find(c => c.nextRouteKey === start.routeKey);
      await dispatch('applyLockedBoundaryDrag', {
        boundaryDay: dayNumber - 1,
        prevRouteKey: connector ? connector.prevRouteKey : null,
        nextRouteKey: start.routeKey,
        targetCoord: target,
      });
      commit('setTrailStartDestination', { ...destination, coordinates: target });
      await dispatch('updateRouteAndMarkers');
      return true;
    }

    commit('setTrailStartDestination', { ...destination, coordinates: target });

    if (!alreadyThere) {
      const oldPosition = [...marker.markerGeometry.coordinates];
      marker.markerGeometry.coordinates = [target[0], target[1], marker.markerGeometry.coordinates[2] ?? 0];
      await dispatch('markerMoved', { marker, oldPosition, routeKey: start.routeKey });
    }

    // The ring-around-the-icon itself is drawn by updateStageConnectorsOnMap
    // (buildHandleData), not updateTrailEndpointMarkersOnMap (which only
    // suppresses the plain pin) — markerMoved already triggers it when the
    // marker had to move, but the common case (a fresh marker already
    // created exactly on the destination — see the 3 "set as start point"
    // dialogs) never moves anything, so it must be called explicitly here
    // too or the ring wouldn't appear until some later action refreshed it.
    await dispatch('addConsolidatedMarkersToMap');
    await dispatch('updateStageConnectorsOnMap');
    await dispatch('updateTrailEndpointMarkersOnMap');
    return true;
  },

  /**
   * Remove the trail-start destination WITHOUT moving any marker — mirrors
   * detachStageDestination.
   */
  async detachTrailStart({ state, commit, dispatch }: PlanTrailActionContext) {
    if (!state.trailStartDestination) return;
    commit('removeTrailStartDestination');

    await dispatch('addConsolidatedMarkersToMap');
    await dispatch('updateStageConnectorsOnMap');
    await dispatch('updateTrailEndpointMarkersOnMap');
  },
};
